import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
  inject,
} from '@angular/core';
import { CalloutComponent, CalloutPlacement } from '@freelancer/ui/callout';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ProgressOrientation } from '@freelancer/ui/progress-bar';
import { textVariants } from '@freelancer/ui/text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { ProgressStepCalloutComponent } from './progress-step-item-callout.component';
import { ProgressStepContentComponent } from './progress-step-item-content.component';
import {
  ProgressStepsBackgroundColor,
  ProgressStepsType,
} from './progress-steps.types';
import { ProgressSubstepComponent } from './progress-substep.component';
import { CalloutContentComponent } from '../callout/callout-content.component';
import { CalloutTriggerComponent } from '../callout/callout-trigger.component';

@Component({
  standalone: true,
  selector: 'fl-progress-step-item',
  imports: [
    CalloutComponent,
    CalloutContentComponent,
    CalloutTriggerComponent,
    IconModule,
    NgTemplateOutlet,
  ],
  template: `
    <ng-template #stepDetails>
      <div
        class="ProgressStepIndicator"
        [class.ProgressStepIndicator-smallDot]="smallDot"
        [attr.ari-label]="indicatorLabel"
        [attr.data-current-step]="currentStep"
        [attr.data-is-done]="done && !skipped"
        [attr.data-is-skipped]="done && skipped"
        [attr.data-orientation]="orientation"
        [attr.data-background-color]="backgroundColor"
        [attr.data-mobile-large-indicator]="mobileLargeIndicator"
      >
        @if (stepItemType === ProgressStepsType.NUMBER) {
          <p
            class="text-color-inherit ItemIndicator font-bold"
            [class.ItemIndicator-notDone]="!done"
          >
            {{ stepNumber }}
          </p>
        } @else {
          @if (smallDot === false) {
            @if (done && !skipped) {
              <fl-icon
                class="ItemIndicator"
                [color]="IconColor.INHERIT"
                [name]="'ui-check'"
                [weight]="FontWeight.BOLD"
                [size]="
                  orientation === ProgressOrientation.HORIZONTAL
                    ? IconSize.XSMALL
                    : IconSize.SMALL
                "
                [sizeTablet]="IconSize.SMALL"
                [useIconFont]="true"
                [fillIconFont]="true"
              ></fl-icon>
            } @else {
              <div
                class="ItemIndicatorDot"
                [class.ItemIndicatorDot-notDone]="!done"
              ></div>
            }
          } @else {
            <div
              class="ItemIndicatorDot ItemIndicatorDotSmall"
              [class.ItemIndicatorDot-notDone]="!done"
            ></div>
          }
        }
      </div>
      <div
        class="ProgressStepText"
        [class.Clickable]="clickable"
        [attr.data-orientation]="orientation"
        [attr.data-mobile-large-indicator]="mobileLargeIndicator"
      >
        <p [class]="stepTitleClasses">
          {{ stepTitle }}
        </p>
      </div>
    </ng-template>
    <ng-template #content>
      @if (clickable) {
        <button
          class="Noborder ProgressStepIconAndTitle Clickable {{
            orientation === ProgressOrientation.HORIZONTAL
              ? 'mb-mid'
              : 'mb-none'
          }} {{
            orientation === ProgressOrientation.HORIZONTAL
              ? 'tablet:mb-large'
              : 'tablet:mb-none'
          }}"
          (click)="stepClicked.emit(this.stepNumber)"
        >
          <ng-container *ngTemplateOutlet="stepDetails"></ng-container>
        </button>
      } @else {
        <div
          class="ProgressStepIconAndTitle {{
            orientation === ProgressOrientation.HORIZONTAL
              ? 'mb-mid'
              : 'mb-none'
          }} {{
            orientation === ProgressOrientation.HORIZONTAL
              ? 'tablet:mb-large'
              : 'tablet:mb-none'
          }}"
        >
          <ng-container *ngTemplateOutlet="stepDetails"></ng-container>
        </div>
      }
      @if (showContent) {
        @if (progressStepContent) {
          <div
            [class]="stepDescriptionClasses"
            [class.mb-small]="!isLastStep"
            [class.mb-none]="isLastStep"
          >
            <ng-content select="fl-progress-step-content"></ng-content>
          </div>
        }
      }
      @if (
        progressSubsteps?.length > 0 &&
        orientation === ProgressOrientation.VERTICAL
      ) {
        <div class="ProgressSubStepContainer">
          <ng-content select="fl-progress-substep"></ng-content>
        </div>
      }
    </ng-template>

    @if (progressStepCallout) {
      <fl-callout
        [hover]="true"
        [placement]="CalloutPlacement.BOTTOM"
      >
        <fl-callout-trigger
          class="ProgressStepCalloutTrigger"
          [attr.data-orientation]="orientation"
        >
          <ng-container *ngTemplateOutlet="content"></ng-container>
        </fl-callout-trigger>
        <fl-callout-content>
          <ng-content select="fl-progress-step-callout"></ng-content>
        </fl-callout-content>
      </fl-callout>
    }

    @if (!progressStepCallout) {
      <ng-container *ngTemplateOutlet="content"></ng-container>
    }
  `,
  styleUrls: ['./progress-step-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressStepItemComponent implements OnChanges, OnInit {
  elementRef = inject(ElementRef);
  changeDetectorRef = inject(ChangeDetectorRef);

  CalloutPlacement = CalloutPlacement;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  ProgressOrientation = ProgressOrientation;
  ProgressStepsType = ProgressStepsType;

  isLastStep: boolean;

  @Input() backgroundColor = ProgressStepsBackgroundColor.DEFAULT;
  @Input() color = FontColor.FOREGROUND;
  @Input() completedSubstep = 0;
  @Input() currentStep = false;
  @Input() done = false;
  @Input() showContent = false;
  @Input() size: TextSize;
  @Input() sizeTablet: TextSize;
  @Input() sizeDesktop: TextSize;
  @Input() skipped = false;
  @Input() stepNumber: number;
  @Input() stepTitle: string;
  @Input() clickable = false;
  @Input() stepItemType = ProgressStepsType.NUMBER;
  @Input() smallDot = false;

  @HostBinding('attr.data-orientation')
  @Input()
  orientation: ProgressOrientation = ProgressOrientation.HORIZONTAL;

  @Input() indicatorLabel?: string;

  @Input()
  mobileLargeIndicator = false;

  /**
   * The title text at the edge will be centered if this is true.
   * Otherwise, it will be left/ right aligned with the indicator.
   */
  @HostBinding('attr.data-center-edge-text')
  @Input()
  centerEdgeText = false;

  @ContentChild(ProgressStepCalloutComponent)
  progressStepCallout: ProgressStepCalloutComponent;

  @ContentChild(ProgressStepContentComponent)
  progressStepContent: ProgressStepContentComponent;

  @ContentChildren(ProgressSubstepComponent)
  progressSubsteps: QueryList<ProgressSubstepComponent>;

  @Output() substepChanged = new EventEmitter<void>();
  @Output() stepClicked = new EventEmitter<number>();

  // FIXME: T300116 Enabling hydration errors on the webapp server
  // TypeError: Cannot read properties of null (reading 'parentElement')
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  stepTitleClasses: string;
  stepDescriptionClasses: string;

  ngOnInit(): void {
    this.sizeTablet = this.sizeTablet
      ? this.sizeTablet
      : (this.size ?? TextSize.BODY_16);
    this.sizeDesktop = this.sizeDesktop ?? this.sizeTablet;
    this.size = this.size
      ? this.size
      : this.orientation === ProgressOrientation.HORIZONTAL
        ? TextSize.BODY_16
        : TextSize.BODY_16;

    this.computeClasses();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      'completedSubstep' in changes &&
      this.progressSubsteps &&
      this.orientation === ProgressOrientation.VERTICAL
    ) {
      this.substepChanged.emit();
    }

    if (
      'color' in changes ||
      'size' in changes ||
      'sizeTablet' in changes ||
      'sizeDesktop' in changes ||
      'orientation' in changes
    ) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.stepTitleClasses = textVariants({
      class: 'ProgressStepTitle',
      color: this.color,
      size: this.size,
      sizeTablet: this.sizeTablet,
      sizeDesktop: this.sizeDesktop,
      weight: FontWeight.BOLD,
    });

    this.stepDescriptionClasses = textVariants({
      class: 'ProgressStepDescription',
      size:
        this.orientation === ProgressOrientation.HORIZONTAL
          ? TextSize.BODY_16
          : TextSize.BODY_16,
      sizeTablet: TextSize.BODY_16,
      color: FontColor.FOREGROUND,
      weight: FontWeight.NORMAL,
    });
  }
}
